// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { apiClient } from "@/queries/client"

// useQueryClient() is used (not the module-level instance) so mutations reach
// the per-test isolated query client in unit tests while still resolving to
// the same singleton in production.

export interface ApiKey {
  uuid?: string | null
  // Server-masked value (`xxxx…xxxx`). Never a full secret.
  key?: string | null
  createdAt?: string
  expirationDate?: string
}

export const API_KEYS_SELF_QUERY_KEY = ["apiKeysSelf"] as const

export const useApiKeysSelf = () => {
  return useQuery<ApiKey[], Error>({
    queryKey: API_KEYS_SELF_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<ApiKey[]>(`/apikeys/self`)
      return response.data
    },
  })
}

export const useCreateApiKey = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient
        .post(`/apikeys/self`, {
          expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_SELF_QUERY_KEY })
    },
  })
}

// 404 is absorbed as success: the key was already revoked from another session,
// so the list invalidation reconciles the stale row. Any other failure rejects.
export const useDeleteApiKey = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) =>
      apiClient
        .delete(`/apikeys/self/${uuid}`)
        .then((res) => res.data)
        .catch((error: unknown) => {
          if (isAxiosError(error) && error.response?.status === 404) {
            return null
          }
          throw error
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_SELF_QUERY_KEY })
    },
  })
}
